import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import detailedQuestions from '../detailed_questions.json';
import mergedVotes from '../merged_votes.json';

// Custom chronological order for show IDs
const CHRONOLOGICAL_SHOW_ORDER = [
  // OSU performances in chronological order
  'osuthu0', 'osuthu1', 'osuthu2', 'osuthu3',
  'osufri1', 'osufri2', 'osufri3',
  'osusat2', 'osusat3',
  'osusun1', 'osusun2', 'osusun3',
  // Tech performances in chronological order
  'techjan16b', 'techjan16c', 'techjan16d',
  'techjan17a', 'techjan17b', 'techjan17d',
  'techjan18h'
];

interface StandardRecord {
  questionNumber: number;
  prompts: {
     [promptID: string]: 
     {
      total: number;
      showCounts: { [showID: string]: number };
    };
  };
}

interface RawVote {
  showID: string;
  ballotID: string;
  promptID: string;
  userInputData: string;
}

// This function standardizes the raw data into a more uniform structure
function standardizeData(rawData: RawVote[]): StandardRecord[] {
  const map = new Map<number, StandardRecord>();
  rawData.forEach(({ showID, ballotID, promptID }) => {
    if (!showID || !ballotID || !promptID) return;
    // Use parseFloat instead of parseInt to correctly handle decimal question numbers like 1020.1
    const qNum = parseFloat(ballotID);
    if (!map.has(qNum)) {
      map.set(qNum, {
        questionNumber: qNum,
        prompts: {},
      });
    }
    const entry = map.get(qNum)!;
    if (!entry.prompts[promptID]) {
      entry.prompts[promptID] = { total: 0, showCounts: {} };
    }
    entry.prompts[promptID].total += 1;
    entry.prompts[promptID].showCounts[showID] =
      (entry.prompts[promptID].showCounts[showID] || 0) + 1;
  });
  // Sort results by question number
  return Array.from(map.values()).sort((a, b) => a.questionNumber - b.questionNumber);
}

const VisHelper = () => {
  // For drill-down
  const navigate = useNavigate();
  const colors = ['#ffb600', '#0095ff', '#00d37b', '#8900f2', '#ed0062', '#C244B7', '#4300ed'];

  // Raw data and standardized data
  const [rawData, setRawData] = useState<RawVote[]>([]);
  const [standardData, setStandardData] = useState<StandardRecord[]>([]);

  // Filter states
  const [selectedShow, setSelectedShow] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<number | ''>('');

  // Handle JSON file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const parsed = JSON.parse(content);
          setRawData(parsed);
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  // Filter and standardize whenever filters or rawData changes
  useEffect(() => {
    uploadHardcodedData();
    let filtered = rawData;
    // Filter by show if selectedShow is not empty
    if (selectedShow) {
      filtered = filtered.filter(r => r.showID === selectedShow);
    }
    // Filter by question if selectedQuestion is not empty
    if (selectedQuestion !== '') {
      filtered = filtered.filter(r => parseFloat(r.ballotID) === selectedQuestion);
    }
    setStandardData(standardizeData(filtered));
  }, [rawData, selectedShow, selectedQuestion]);

  // Build grouped data for chart
  const dataByShowID = useMemo(() => {
    const grouped: { [showID: string]: { [ballotID: string]: number } } = {};
    let relevant = rawData;

    if (selectedShow) {
      relevant = relevant.filter(r => r.showID === selectedShow);
    }
    if (selectedQuestion !== '') {
      relevant = relevant.filter(r => parseFloat(r.ballotID) === selectedQuestion);
    }

    relevant.forEach(({ showID, ballotID }) => {
      if (!showID || !ballotID) return;
      if (!grouped[showID]) grouped[showID] = {};
      if (!grouped[showID][ballotID]) grouped[showID][ballotID] = 0;
      grouped[showID][ballotID] += 1;
    });
    return grouped;
  }, [rawData, selectedShow, selectedQuestion]);

  const showIDs = useMemo(() => {
    // Get filtered show IDs
    const filteredShowIDs = Object.keys(dataByShowID)
      .filter(showID => showID !== "null" && showID !== null && showID !== undefined);
    
    // Sort based on the custom order
    return filteredShowIDs.sort((a, b) => {
      const indexA = CHRONOLOGICAL_SHOW_ORDER.indexOf(a);
      const indexB = CHRONOLOGICAL_SHOW_ORDER.indexOf(b);
      
      // If both IDs are in the custom order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one ID is in the custom order, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // For any IDs not in the custom order, use default alphabetical sorting
      return a.localeCompare(b, 'en', { numeric: true });
    });
  }, [dataByShowID]);

  // Add this useMemo to get all prompts used by each question for the selected show
  const promptsByQuestion = useMemo(() => {
    const mapping: { [questionNumber: number]: string[] } = {};
    
    if (selectedShow !== '') {
      rawData
        .filter(vote => vote.showID === selectedShow)
        .forEach(vote => {
          const qNum = parseFloat(vote.ballotID);
          if (!mapping[qNum]) mapping[qNum] = [];
          if (!mapping[qNum].includes(vote.promptID)) {
            mapping[qNum].push(vote.promptID);
          }
        });
      
      // Sort the prompts for each question
      Object.keys(mapping).forEach(qNum => {
        mapping[parseFloat(qNum)].sort();
      });
    }
    
    return mapping;
  }, [rawData, selectedShow]);

  // Modify the chartData useMemo
  const chartData = useMemo(() => {
    const data: { questionNumber: number; [key: string]: any }[] = [];
    const ballotIDs = Array.from(
      new Set(
        Object.keys(dataByShowID)
          .flatMap(show => Object.keys(dataByShowID[show]).map(Number))
      )
    ).sort((a, b) => a - b);

    ballotIDs.forEach(ballotID => {
      if (ballotID > 1025) return;
      const dataPoint: { questionNumber: number; [key: string]: any } = {
        questionNumber: ballotID,
      };

      // When a specific question is selected, show stacked bars
      if (selectedQuestion !== '' && selectedQuestion === ballotID) {
        showIDs.forEach(showID => {
          // Get all prompts for this question and show
          const promptsForQuestion = Array.from(new Set(
            rawData
              .filter(vote => parseFloat(vote.ballotID) === ballotID && vote.showID === showID)
              .map(vote => vote.promptID)
          )).sort();
          
          // Add data for each prompt as a stackable segment
          promptsForQuestion.forEach(promptID => {
            const count = rawData.filter(
              vote => vote.showID === showID && 
                    parseFloat(vote.ballotID) === ballotID && 
                    vote.promptID === promptID
            ).length;
            
            dataPoint[`${showID}_${promptID}`] = count;
          });
        });
      }
      // When a specific show is selected (but no specific question)
      else if (selectedShow !== '') {
        // Get all prompts for this question and the selected show
        const promptsForQuestion = promptsByQuestion[ballotID] || [];
        
        // Add data for each prompt option
        promptsForQuestion.forEach(promptID => {
          const count = rawData.filter(
            vote => vote.showID === selectedShow && 
                  parseFloat(vote.ballotID) === ballotID && 
                  vote.promptID === promptID
          ).length;
          
          // Store count by prompt ID
          dataPoint[`prompt_${promptID}`] = count;
        });
      }
      else {
        // Regular data for non-selected questions
        showIDs.forEach(showID => {
          // Get total votes for this show and question
          const totalVotes = rawData.filter(
            vote => vote.showID === showID && parseFloat(vote.ballotID) === ballotID
          ).length;
          dataPoint[showID] = totalVotes;
        });
      }
      
      data.push(dataPoint);
    });
    
    return data;
  }, [dataByShowID, showIDs, rawData, selectedQuestion, selectedShow, promptsByQuestion]);

  // Get prompt IDs for selected question
  const promptsForSelectedQuestion = useMemo(() => {
    if (selectedQuestion === '') return [];
    return Array.from(new Set(
      rawData
        .filter(vote => parseFloat(vote.ballotID) === selectedQuestion)
        .map(vote => vote.promptID)
    )).sort();
  }, [rawData, selectedQuestion]);

  // Handle bar click for drill-down
  const handleBarClick = useCallback(
    (questionNumber: number) => {
      const showData = showIDs.reduce((acc, show) => {
        const count = dataByShowID[show][questionNumber.toString()] || 0;
        acc[show] = count;
        return acc;
      }, {} as { [key: string]: number });
      navigate('/drill-down', { state: { questionNumber, showData, rawData } });
    },
    [showIDs, dataByShowID, navigate, rawData]
  );

  // Custom tooltip with prompt breakdown for all shows
  const CustomTooltip = ({ active, payload, label }: any) => {
    if  (active && payload && payload.length) {
      // Case 1: When BOTH a specific question AND a specific show are selected
      if (selectedQuestion !== '' && selectedShow !== '') {
        const questionText = detailedQuestions[selectedQuestion.toString()]?.Description || `Question ${selectedQuestion}`;
        const prompts = detailedQuestions[selectedQuestion.toString()]?.Prompts || {};
        
        // Sort prompts to ensure consistent ordering with the chart
        const sortedPromptIDs = promptsForSelectedQuestion.sort();
        
        return (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #444',
            padding: '12px',
            borderRadius: '6px',
            maxWidth: '600px',
            minWidth: '300px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
              {questionText} - Show {selectedShow}
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              width: '100%'
            }}>
              {payload.map((entry, i) => {
                // Extract prompt ID from the dataKey
                const promptID = entry.dataKey.split('_')[1];
                const promptName = prompts[promptID]?.Name || `Option ${promptID}`;
                
                // Use the same color logic as the bar chart
                const colorIndex = sortedPromptIDs.indexOf(promptID) % colors.length;
                
                return (
                  <div key={promptID} style={{ 
                    marginBottom: '10px', 
                    flex: '1 1 calc(33% - 10px)',
                    minWidth: '150px',
                  }}>
                    <p style={{ 
                      fontWeight: 'bold', 
                      margin: '5px 0', 
                      fontSize: '14px'
                    }}>
                      {promptName}:
                    </p>
                    <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '11px' }}>
                      <li style={{ margin: '2px 0' }}>
                        <span style={{ 
                          display: 'inline-block', 
                          width: '10px', 
                          height: '10px',
                          backgroundColor: colors[colorIndex],
                          marginRight: '5px' 
                        }}></span>
                        Votes: {entry.value}
                      </li>
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      
      // Special case for question selected but no show selected
      else if (selectedQuestion !== '' && selectedShow === '' && typeof label === 'string') {
        // In this case, label is the showID
        const showID = label;
        const questionText = detailedQuestions[selectedQuestion.toString()]?.Description || `Question ${selectedQuestion}`;
        const prompts = detailedQuestions[selectedQuestion.toString()]?.Prompts || {};
        
        // Create a mapping of promptID to vote count for this specific show
        const promptVotes: { [promptID: string]: number } = {};
        
        // Extract data from payload
        payload.forEach(entry => {
          const promptID = entry.dataKey.split('_')[1];
          promptVotes[promptID] = entry.value;
        });
        
        // Sort prompts to ensure consistent ordering with the chart
        const sortedPromptIDs = promptsForSelectedQuestion.sort();
        
        return (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #444',
            padding: '12px',
            borderRadius: '6px',
            maxWidth: '600px',
            minWidth: '300px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
              {questionText} - Show {showID}
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              width: '100%'
            }}>
              {sortedPromptIDs.map(promptID => (
                <div key={promptID} style={{ 
                  marginBottom: '10px', 
                  flex: '1 1 calc(33% - 10px)',
                  minWidth: '150px',
                }}>
                  <p style={{ 
                    fontWeight: 'bold', 
                    margin: '5px 0', 
                    fontSize: '14px'
                  }}>
                    {prompts[promptID]?.Name || `Option ${promptID}`}:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '11px' }}>
                    <li style={{ margin: '2px 0' }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '10px', 
                        height: '10px',
                        backgroundColor: colors[sortedPromptIDs.indexOf(promptID) % colors.length],
                        marginRight: '5px' 
                      }}></span>
                      Votes: {promptVotes[promptID] || 0}
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );
      } 
      // Case 2: When a specific show is selected but no question
      else if (selectedShow !== '' && selectedQuestion === '') {
        const questionNumber = parseFloat(label.toString());
        const questionText = detailedQuestions[questionNumber.toString()]?.Description || `Question ${questionNumber}`;
        const prompts = detailedQuestions[questionNumber.toString()]?.Prompts || {};
      
        const promptVotesObj: { [promptID: string]: number } = {};
          payload.forEach(entry => {
            const promptID = entry.dataKey.split('_')[1];
            promptVotesObj[promptID] = entry.value;
        });
        
        return (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #444',
            padding: '12px',
            borderRadius: '6px',
            maxWidth: '600px',
            minWidth: '300px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
            {questionText}
          </p>
            <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            width: '100%'
          }}>
            {Object.keys(promptVotesObj).map(promptID => (
              <div key={promptID} style={{ 
                marginBottom: '10px', 
                flex: '1 1 calc(33% - 10px)',
                minWidth: '150px',
              }}>
                <p style={{ 
                  fontWeight: 'bold', 
                  margin: '5px 0', 
                  fontSize: '14px'
                }}>
                  {prompts[promptID]?.Name || `Option ${promptID}`}:
                </p>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '11px' }}>
                  <li style={{ margin: '2px 0' }}>
                    <span style={{ 
                      display: 'inline-block', 
                      width: '10px', 
                      height: '10px',
                      backgroundColor: payload.find(p => p.dataKey === `prompt_${promptID}`)?.color,
                      marginRight: '5px' 
                    }}></span>
                    Votes: {promptVotesObj[promptID]}
                  </li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      );
    }
      // Original case from your provided code
      else {
        const questionNumber = parseFloat(label.toString());
        const questionText = detailedQuestions[label.toString()]?.Description || `Question ${label}`;
        
        // Get prompt options for this question
        const prompts = detailedQuestions[label.toString()]?.Prompts || {};
        
        // Group votes by promptID and showID for this question
        const promptVotes: { [promptID: string]: { [showID: string]: number } } = {};
        
        rawData.forEach(vote => {
          if (parseFloat(vote.ballotID) === questionNumber) {
            if (!promptVotes[vote.promptID]) {
              promptVotes[vote.promptID] = {};
            }
            if (!promptVotes[vote.promptID][vote.showID]) {
              promptVotes[vote.promptID][vote.showID] = 0;
            }
            promptVotes[vote.promptID][vote.showID]++;
          }
        });

        return (
          <div
            style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #444',
              padding: '12px',
              borderRadius: '6px',
              maxWidth: '600px',
              minWidth: '300px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <p style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px', wordWrap: 'break-word',  whiteSpace: 'normal', }}>
              {questionText}
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              width: '100%'
            }}>
            {Object.keys(promptVotes).map(promptID => (
              <div key={promptID} style={{ marginBottom: '10px', flex: '1 1 calc(33% - 10px)',
                minWidth: '150px',
              }}>
                <p style={{ 
                  fontWeight: 'bold', margin: '5px 0', fontSize: '14px'}}>
                  {prompts[promptID]?.Name || `Option ${promptID}`}:
                </p>
                <ul style={{ margin: '0', paddingLeft: '20px',fontSize: '11px' }}>
                  {showIDs.map(showID => (
                    <li key={showID} style={{ margin: '2px 0' }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '10px', 
                        height: '10px', 
                        backgroundColor: colors[showIDs.indexOf(showID) % colors.length], 
                        marginRight: '5px' 
                      }}></span>
                      Show {showID}: {promptVotes[promptID]?.[showID] || 0} votes
                    </li>
                    
                  ))}
                </ul>
              </div>
            ))}
            </div>
          </div>
        );
      }
    }
    return null;
  };

  // Custom legend with show-based navigation
  const CustomLegend = ({ payload }: any) => {
    const nav = useNavigate();
    
    const handleClick = (value: string) => {
      // Extract showID from the name (before "_" if it exists)
      const showID = value.includes('_') ? value.split('_')[0] : value;
      nav('/vis-by-show', { state: { showID, rawData } });
    };
    
    // Filter payload to only have unique show IDs
    const uniqueShows = new Map();
    payload?.forEach((entry: any) => {
      const value = entry.value;
      // Extract just the show ID part (before "_" if it exists)
      const showID = value.includes('_') ? value.split('_')[0] : value;
      
      if (!uniqueShows.has(showID)) {
        uniqueShows.set(showID, {
          value: showID,
          color: entry.color
        });
      }
    });
    
    const uniqueEntries = Array.from(uniqueShows.values());
    
    return (
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center'}}>
        {uniqueEntries.slice()
        .sort((a, b) => {
          const indexA = CHRONOLOGICAL_SHOW_ORDER.indexOf(a.value);
          const indexB = CHRONOLOGICAL_SHOW_ORDER.indexOf(b.value);
          
          // If both IDs are in the custom order, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          
          // If only one ID is in the custom order, prioritize it
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          
          // For any IDs not in the custom order, use default alphabetical sorting
          return a.value.localeCompare(b.value);
        }).map((entry: any, index: number) => (
          <li 
            key={`item-${index}`}
            style={{ 
              display: 'inline-block', 
              marginRight: 10, 
              cursor: 'pointer',
              color: entry.color
            }}
            onClick={() => handleClick(entry.value)}
          >
            <span style={{ marginRight: 5, display: 'inline-block', width: 10, height: 10, backgroundColor: entry.color }}></span>
            {entry.value}
          </li>
        ))}
      </ul>
    );
  };

  // All unique shows for dropdown
  const allShows = useMemo(() => {
    const filteredShows = Array.from(new Set(rawData.map(r => r.showID)))
      .filter(showID => showID !== "null" && showID !== null && showID !== undefined);
    
    // Sort based on the custom order
    return filteredShows.sort((a, b) => {
      const indexA = CHRONOLOGICAL_SHOW_ORDER.indexOf(a);
      const indexB = CHRONOLOGICAL_SHOW_ORDER.indexOf(b);
      
      // If both IDs are in the custom order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one ID is in the custom order, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // For any IDs not in the custom order, use default alphabetical sorting
      return a.localeCompare(b, 'en', { numeric: true });
    });
  }, [rawData]);

  // All unique question numbers for dropdown
  const allQuestionNumbers = useMemo(() => {
    const qNums = new Set<number>();
    rawData.forEach(r => {
      const q = parseFloat(r.ballotID);
      if (q && q <= 1025) {
        qNums.add(q);
      }
    });
    return Array.from(qNums).sort((a, b) => a - b);
  }, [rawData]);

  // Prompt counts for selected question
  const promptCounts = useMemo(() => {
    if (selectedQuestion === '') return [];
    const counts: Array<{ promptID: string; description?: string; showCounts: { [showID: string]: number } }> = [];
    
    rawData.forEach(({ showID, ballotID, promptID }) => {
      if (parseFloat(ballotID) === selectedQuestion) {
        let entry = counts.find(c => c.promptID === promptID);
        if (!entry) {
          // Add prompt description from detailed_questions
          const description = detailedQuestions[selectedQuestion.toString()]?.
            Prompts?.[promptID]?.Description || '';
          
          entry = { promptID, description, showCounts: {} };
          counts.push(entry);
        }
        
        // Use non-null assertion to satisfy TypeScript
        entry!.showCounts[showID] = (entry!.showCounts[showID] || 0) + 1;
      }
    });
    return counts;
  }, [rawData, selectedQuestion]);

  const uploadHardcodedData = () => {
    const jsonString = JSON.stringify(mergedVotes);
    const blob = new Blob([jsonString], { type: 'application/json' });

    const reader = new FileReader();
    reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const parsed = JSON.parse(content);
          setRawData(parsed);
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      };
    reader.readAsText(blob);
  }

  return (
    <div className="App">
      <header className="App-header">

        {rawData.length > 0 && (
          <>
            {/* Filter by show */}
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="showSelect" style={{ marginRight: 10 }}>
                Filter by show:
              </label>
              <select
                id="showSelect"
                value={selectedShow}
                onChange={(e) => setSelectedShow(e.target.value)}
              >
                <option value="">All Shows</option>
                {allShows.map((show) => (
                  <option key={show} value={show}>
                    {show}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter by question number */}
            <div style={{ marginBottom: 20 }}>
              <label htmlFor="questionSelect" style={{ marginRight: 10 }}>
                Filter by question:
              </label>
              <select
                id="questionSelect"
                value={selectedQuestion === '' ? '' : selectedQuestion}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedQuestion(val === '' ? '' : Number(val));
                }}
              >
                <option value="">All Questions</option>
                {allQuestionNumbers.map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            {/* Chart container */}
            <ResponsiveContainer width="95%" height={400}>
              {selectedQuestion !== '' && selectedShow === '' ? (
                // Special case: When a specific question is selected but no specific show
                // Shows on x-axis, prompts as bars
                <BarChart
                  data={showIDs.map(showID => {
                    const dataPoint: { [key: string]: any } = { showID };
                    
                    // Add data for each prompt for this show and selected question
                    promptsForSelectedQuestion.forEach(promptID => {
                      const count = rawData.filter(
                        vote => vote.showID === showID && 
                              parseFloat(vote.ballotID) === selectedQuestion && 
                              vote.promptID === promptID
                      ).length;
                      
                      dataPoint[`prompt_${promptID}`] = count;
                    });
                    
                    return dataPoint;
                  })}
                  margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
                  barGap={0.5}
                >
                  <XAxis 
                    dataKey="showID" 
                    tick={{ fontSize: 13 }}
                    label={{ value: 'Show ID', position: 'insideBottomRight', offset: -12 }}
                    interval={0}
                  />
                  <YAxis 
                    tick={{ fontSize: 14 }}
                    label={{ value: 'Votes', angle: -90, position: 'insideLeft', offset: -20 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" align="center" />

                  
                  {/* Render bars for each prompt */}
                  {promptsForSelectedQuestion.map((promptID, i) => {
                    const promptName = detailedQuestions[selectedQuestion.toString()]?.Prompts?.[promptID]?.Name || `Option ${promptID}`;
                    return (
                      <Bar
                        key={`prompt_${promptID}`}
                        dataKey={`prompt_${promptID}`}
                        name={promptName}
                        fill={colors[i % colors.length]}
                        cursor="pointer"
                      />
                    );
                  })}
                </BarChart>
              ) : (
                // Original chart for other cases
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
                  barGap={0.5}
                  onClick={data => {
                    if (data && data.activePayload) {
                      handleBarClick(data.activePayload[0].payload.questionNumber);
                    }
                  }}
                >
                  <XAxis 
                    dataKey="questionNumber" 
                    tick={{ fontSize: 13 }}
                    label={{ value: 'Question Number', position: 'insideBottomRight', offset: -12 }}
                    interval={0}
                  />
                  <YAxis 
                    tick={{ fontSize: 14 }}
                    label={{ value: 'Votes', angle: -90, position: 'insideLeft', offset: -20 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {/* Only show legend when NOT in the "show selected but no question" state */}
                  {!(selectedShow !== '' && selectedQuestion === '') && (
                    <Legend content={<CustomLegend />} verticalAlign="bottom" />
                  )}
                  
                  {selectedQuestion !== '' ? (
                    // When a specific question is selected and a specific show is selected
                    showIDs.map((showID) => (
                      promptsForSelectedQuestion.map((promptID, j) => {
                        const promptName = detailedQuestions[selectedQuestion.toString()]?.Prompts?.[promptID]?.Name || `Option ${promptID}`;
                        return (
                          <Bar
                            key={`${showID}_${promptID}`}
                            dataKey={`${showID}_${promptID}`}
                            // stackId={showID}
                            name={`${showID}_${promptID}`}
                            fill={colors[j % colors.length]}
                            onClick={() => handleBarClick(selectedQuestion)}
                            cursor="pointer"
                          />
                        );
                      })
                    ))
                  ) : selectedShow !== '' ? (
                    // When a specific show is selected (but no specific question)
                    Array.from(new Set(
                      Object.values(promptsByQuestion).flat()
                    )).sort().map((promptID, i) => {
                      const promptName = `Option ${promptID}`;
                      return (
                        <Bar
                          key={`prompt_${promptID}`}
                          dataKey={`prompt_${promptID}`}
                          name={promptName}
                          fill={colors[i % colors.length]}
                          onClick={(data) => handleBarClick(data.questionNumber)}
                          cursor="pointer"
                        />
                      );
                    })
                  ) : (
                    // Regular chart when no question or show is selected
                    showIDs.map((show, i) => (
                      <Bar
                        key={show}
                        dataKey={show}
                        fill={colors[i % colors.length]}
                        name={show}
                        onClick={(data) => handleBarClick(data.questionNumber)}
                        cursor="pointer"
                        stackId="a"
                      />
                    ))
                  )}
                </BarChart>
              )}
            </ResponsiveContainer>
          </>
        )}
      </header>
    </div>
  );
};

export default VisHelper;